import { Box, Button, Stack, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { GroupSettingsContext } from "../../../contexts/GroupSettingsContext";
import { defaultStages } from "../../../shared/dashboard";
import MultiSelector from "../../atoms/MultiSelector";

const useStyles = makeStyles((theme) => ({
    title: {
        minWidth: 70,
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['700'],
    },
    label: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 600,
        color: theme.palette.primary.main,
    },
    clear: {
        minWidth: 80,
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 600,
        color: theme.palette.warning.dark,
        textTransform: 'none !important' as any,
        transition: 'ease-in-out 300ms',
        "&:hover": {
            background: 'transparent',
        }
    },
    count: {
        minWidth: 100,
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: '500',
        color: theme.colors.neutral['500'],
    },
    cropped: {
        maxWidth: 300,
        minHeight: 35,
        maxHeight: 70,
        overflowX: 'hidden',
        overflowY: 'auto',
    },
}));

const DashboardsTableFilter: React.FC<{
    count?: number,
    recentType?: string,
    onFilter?: ({ statuses, sectors, stages } : { statuses?: string[], sectors?: string[], stages?: string[] }) => void,
}> = ({ count, recentType, onFilter }) => {
    const classes = useStyles();
    const { statuses, tags } = useContext(GroupSettingsContext);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
    const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
    const [selectedStages, setSelectedStages] = useState<string[]>([]);

    const statusesOptions = useMemo(() =>
        statuses.map(status => status.charAt(0).toUpperCase() + status.slice(1))
    , [statuses]);

    const hasSelection = useMemo(() =>
        !![...selectedStatuses, ...selectedSectors, ...selectedStages].length
    , [selectedStatuses, selectedSectors, selectedStages]);

    const handleFilter = useCallback((selections: string[], type?: string) => {
        switch (type) {
            case 'statuses':
                setSelectedStatuses(selections);
                onFilter?.({ statuses: selections });
                break;
            case 'sectors':
                setSelectedSectors(selections);
                onFilter?.({ sectors: selections });
                break;
            case 'stages':
                setSelectedStages(selections);
                onFilter?.({ stages: selections });
                break;
            default:
                setSelectedStatuses([]);
                setSelectedSectors([]);
                setSelectedStages([]);
                onFilter?.({ statuses: [], sectors: [], stages: [] });
                break;
        }
    }, [onFilter]);

    return (<>
        <Stack direction="row" alignItems="center"
            justifyContent={!!count ? 'space-between' : 'flex-start'} width={!onFilter ? '180px' : '100%'}>
            {!!onFilter ? (
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
                    <Typography className={classes.title}>{'View by:'}</Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center" display="none">
                            <Typography className={classes.label}>
                                {'statuses: '}
                            </Typography>
                            <Box className={classes.cropped}>
                                <MultiSelector
                                    mainOptions={statusesOptions}
                                    selections={selectedStatuses}
                                    onSelection={(selections) => handleFilter(selections as string[], 'statuses')}
                                    fullSpan
                                    withSearch />
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography className={classes.label}>
                                {'sector: '}
                            </Typography>
                            <Box className={classes.cropped}>
                                <MultiSelector
                                    mainOptions={tags}
                                    selections={selectedSectors}
                                    onSelection={(selections) => handleFilter(selections as string[], 'sectors')}
                                    fullSpan
                                    withSearch />
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography className={classes.label}>
                                {'stage: '}
                            </Typography>
                            <Box className={classes.cropped}>
                                <MultiSelector
                                    mainOptions={defaultStages}
                                    selections={selectedStages}
                                    onSelection={(selections) => handleFilter(selections as string[], 'stages')}
                                    fullSpan />
                            </Box>
                        </Stack>
                        {hasSelection && (
                            <Button variant="text"
                                className={classes.clear}
                                onClick={() => handleFilter([])}>
                                {'clear all'}
                            </Button>
                        )}
                    </Stack>
                </Stack>
            ) : (<Box height={40} />)}
            {!!count && (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" ml={2}>
                    <Typography
                        fontFamily="Poppins"
                        fontSize="1.5rem"
                        fontWeight="bold"
                        color="#000">
                        {count}
                    </Typography>
                    <Typography className={classes.count} textTransform="lowercase">
                        {!!recentType ? ` ${count > 1 && recentType.endsWith('y') 
                            ? recentType.slice(0, -1) + 'ies' 
                            : recentType + (count > 1 ? 's' : '')}`
                            : ` compan${count > 1 ? 'ies' : 'y'}`}
                    </Typography>
                </Stack>
            )}
        </Stack>
    </>);
}

export default DashboardsTableFilter;